import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import { Helmet } from 'react-helmet-async';

import type { Metadata } from '@/types/v2/metadata';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { useUser } from '@/hooks/v2/use-user';
import { RoleGuard } from '@/components/auth/guards/role-guard';

import { CommunitySettingsHeader } from './community-settings-header';
import { SideNav } from './side-nav';

interface LayoutProps {
  children: React.ReactNode;
  metadata: Metadata;
}

export function Layout({ children, metadata }: LayoutProps): React.JSX.Element {
  const { community } = useUser();
  return (
    <RoleGuard
      redirect
      requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
      requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]}
    >
      <Helmet>
        <title>{metadata.title}</title>
      </Helmet>
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <CommunitySettingsHeader
              community={community!}
              error={null}
              isLoading={false}
            />
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ position: 'relative' }}>
            <SideNav />
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{children}</Box>
          </Stack>
        </Container>
      </Box>
    </RoleGuard>
  );
}

export { Layout as CommunitySettingsLayout };
